/**
 * Update date: 09-06-2023
 * Popup select vendor component
 */
import { DataGridFull } from 'app/components/DataGrid';
import useAxios from 'axios-hooks';
import { Button, Popup, ScrollView, TextBox } from 'devextreme-react';
import LoadPanel from 'app/components/LoadPanel';
import useDictionary from 'hooks/useDictionary';
import useFormatTime from 'hooks/useFormatTime';
import React, { useState, useRef, useEffect } from 'react';
import { isMobile } from 'react-device-detect';
import { useApp } from 'app';
import { notification } from 'utils/notification';

const defaultParamsVendor = {
  use: [true],
  vendorType: [],
  pageIndex: 1,
  pageSize: 10,
  vendorName: '',
};
interface IPopupSelectVendor {
  visible?: boolean;
  onHiding?: () => void;
  text?: string;
  onSubmit?: void | any;
  mode?: 'single' | 'multiple';
  title?: string;
  urlVendor?: string;
}

function PopupSelectVendor({
  visible,
  onHiding,
  text,
  onSubmit,
  mode = 'single',
  title,
  urlVendor,
}: IPopupSelectVendor) {
  const { themePro }: any = useApp();
  const [vendorName, setVendorName] = useState(text);
  const [data, setData] = useState<any>([]);
  const dataRef: any = useRef(null);
  const { DateTimeFormat } = useFormatTime();
  const { t } = useDictionary({});

  const [{ loading }, fetchData] = useAxios(
    {
      url: urlVendor || '/user/vendor',
      method: urlVendor ? 'GET' : 'POST',
    },
    {
      manual: true,
      useCache: false,
      autoCancel: true,
    },
  );

  /**
   * on search
   *
   */
  const onSearch = async () => {
    const res = await fetchData({
      data: { ...defaultParamsVendor, vendorName },
    });
    if (res?.data?.status === '200') {
      setData(res?.data?.data || []);
      if (mode === 'single') {
        setTimeout(() => {
          const gridInstance = dataRef?.current?.instance;
          gridInstance?.clearSelection?.();
          gridInstance?.selectRows(gridInstance?.getKeyByRowIndex?.(0));
          gridInstance?.focus?.();
        }, 600);
      }
    } else {
      notification({ res });
    }
  };

  /**
   * on ok
   *
   */
  const onOk = () => {
    const selectedRowKeys =
      dataRef?.current?.instance?.option?.()?.selectedRowKeys;
    if (!mode || mode === 'single') {
      const d = selectedRowKeys?.[0];
      onSubmit(d);
    }
    if (mode === 'multiple') {
      onSubmit(selectedRowKeys);
    }
  };

  useEffect(() => {
    onSearch();
  }, []);

  const loadingAll = loading;

  /**
   * render content
   *
   * @return {*}
   */
  const content = () => {
    return (
      <ScrollView width="100%" height="100%">
        <div style={{ padding: '0 15px' }}>
          <LoadPanel visible={loadingAll} />
          {themePro && (
            <div className="modal-popup-header">
              <span className="title-page">{t('Vendor')}</span>
              <div>
                <Button
                  stylingMode="contained"
                  type="default"
                  text={t('OK')}
                  icon="check"
                  style={{ marginRight: 5 }}
                  onClick={onOk}
                />
                <Button
                  stylingMode="contained"
                  text={t('Cancel')}
                  icon="close"
                  onClick={onHiding}
                />
              </div>
            </div>
          )}
          <div
            style={
              themePro
                ? {
                    padding: 8,
                    backgroundColor: '#fff',
                    borderRadius: 6,
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: 20,
                    boxShadow:
                      '0px 1px 3px rgba(16, 24, 40, 0.1), 0px 1px 2px rgba(16, 24, 40, 0.06)',
                  }
                : {
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    marginBottom: 20,
                  }
            }
          >
            <TextBox
              width={themePro ? 400 : '40vw'}
              defaultValue={vendorName}
              onValueChanged={e => setVendorName(e?.value)}
              onEnterKey={onSearch}
              style={{ marginRight: 10 }}
            />
            <Button icon="search" text={t('Search')} onClick={onSearch} />
          </div>
          <DataGridFull
            ref={dataRef}
            dataSource={data}
            fixedLeft={0}
            fixedRight={0}
            columns={[
              {
                dataField: 'vendorId',
                caption: t('ID'),
                alignment: 'left',
              },
              {
                dataField: 'vendorType',
                caption: t('Vendor Type'),
              },
              {
                dataField: 'businessNumber',
                caption: t('Business Number'),
              },
              {
                dataField: 'vendorName',
                caption: t('Vendor'),
              },
              {
                dataField: 'vendorRepresentative',
                caption: t('Representative'),
              },
              {
                dataField: 'vendorEmail',
                caption: t('Email'),
              },
              {
                dataField: 'vendorMobile',
                caption: t('Mobile No'),
              },
              {
                dataField: 'vendorPhone',
                caption: t('Phone No'),
              },
              {
                dataField: 'createdDate',
                caption: t('Regist date and time'),
                dataType: 'date',
                format: DateTimeFormat,
              },
            ]}
            options={{
              // keyExpr: 'code',
              onRowDblClick: onOk,
              onKeyDown: e => {
                const keyCode = e?.event?.keyCode;
                if (keyCode === 38 || keyCode === 40) {
                  if (mode !== 'single') return;
                  const selKey = e?.component?.getSelectedRowKeys();
                  if (selKey?.length) {
                    const currentKey = selKey[0];
                    let index =
                      e?.component?.getRowIndexByKey?.(currentKey) || 0;
                    if (keyCode === 38) {
                      index--;
                      if (index >= 0) {
                        e?.component?.selectRowsByIndexes([index]);
                      }
                    } else if (
                      keyCode === 40 &&
                      index + 1 !== e?.component?.getVisibleRows?.()?.length
                    ) {
                      index++;
                      e?.component?.selectRowsByIndexes([index]);
                    }
                    e?.component?.navigateToRow(
                      e?.component.getKeyByRowIndex(index),
                    );
                  }
                  e?.event?.preventDefault();
                  e?.event?.stopPropagation();
                } else if (keyCode === 13) {
                  onOk();
                  e?.event?.preventDefault();
                  e?.event?.stopPropagation();
                }
              },
              onToolbarPreparing: (e: any) => {
                e.toolbarOptions.items.unshift({
                  location: 'before',
                  template: 'totalCount',
                });
              },
              selection: {
                mode,
              },
              height: themePro
                ? 'calc(100vh - 200px)'
                : isMobile
                ? 'calc(80vh - 200px)'
                : 'calc(70vh - 200px)',
              columnAutoWidth: true,
            }}
          />
          {!themePro && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 20,
              }}
            >
              <Button icon="save" text={t('OK')} onClick={onOk} />
              <Button icon="close" text={t('Cancel')} onClick={onHiding} />
            </div>
          )}
        </div>
      </ScrollView>
    );
  };

  return (
    <Popup
      className="modal-content-popup"
      visible={visible}
      onHiding={onHiding}
      title={title || t('Vendor')}
      contentRender={content}
      height={themePro ? '100vh' : isMobile ? '80vh' : '70vh'}
      width={isMobile ? '96%' : '60vw'}
    />
  );
}

export default PopupSelectVendor;
